import React, { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  HStack,
  Link,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'

import axios from 'lib/axios'

import InfoHint from '../components/InfoHint'
import { formatDisplayDate, getErrorMessage } from '../types'

type Props = {
  statementEndDate: string
}

const STATEMENTS_SYNC_WORKFLOW_URL =
  'https://console.cloud.google.com/workflows/workflow/us-central1/statements-sync/executions?project=homevest-production'

const SyncStatements = ({ statementEndDate }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<any | null>(null)

  const onRefresh = async () => {
    try {
      setIsLoading(true)
      setError('')
      const { data } = await axios.get('/admin/statements/sync-status', {
        params: { statementEndDate }
      })
      setResult(data)
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack spacing={3}>
      <HStack spacing={1} alignSelf='flex-start' alignItems='center'>
        <Link href={STATEMENTS_SYNC_WORKFLOW_URL} isExternal color='teal.600'>
          Open statements-sync Workflow
        </Link>
        <InfoHint label='This workflow runs the statements-sync Cloud Run job, which copies refreshed statement rows from BigQuery property_financials_final.monthly_customer_statements into Postgres statements_raw.' />
      </HStack>
      <HStack spacing={1} alignSelf='flex-start' alignItems='center'>
        <Button
          colorScheme='teal'
          variant='outline'
          onClick={onRefresh}
          isDisabled={isLoading || !statementEndDate}
          alignSelf='flex-start'
        >
          Refresh Sync Status
        </Button>
        <InfoHint label='Checks if statements_raw is synchronized for your selected statement month by comparing Postgres and BigQuery row counts.' />
      </HStack>
      {isLoading && <Spinner size='sm' />}

      {error && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <>
          <HStack spacing={1} alignItems='center'>
            <Text>Postgres max statement date: {formatDisplayDate(result.maxStatementDate)}</Text>
            <InfoHint label='Latest statement date currently present in Postgres statements_raw.' />
          </HStack>
          <HStack spacing={1} alignItems='center'>
            <Text>Postgres count: {result.postgresCount}</Text>
            <InfoHint label='Number of rows in Postgres statements_raw for the selected statement date.' />
          </HStack>
          <HStack spacing={1} alignItems='center'>
            <Text>BigQuery final count: {result.bigQueryFinalCount}</Text>
            <InfoHint label='Number of rows in BigQuery property_financials_final.monthly_customer_statements for the selected statement date.' />
          </HStack>
          <Alert status={result.inSync ? 'success' : 'warning'}>
            <AlertIcon />
            <AlertDescription>
              {result.inSync
                ? 'statements_raw is in sync with BigQuery final.'
                : 'statements_raw is not yet in sync.'}
            </AlertDescription>
          </Alert>
        </>
      )}
    </Stack>
  )
}

export default SyncStatements
